import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/navbar.css';

interface NavLink {
  text: string;
  href: string;
}

interface NavbarProps {
  logoImage?: string;
  logoText?: string;
  ctaText?: string;
  ctaHref?: string;
  navLinks?: NavLink[];
}

const Navbar: React.FC<NavbarProps> = ({
  logoImage = "https://via.placeholder.com/40",
  logoText = "Muse Legal Group",
  ctaText = "Free consult",
  ctaHref = "/contact",
  navLinks = [
    { text: "Home", href: "/" },
    { text: "About", href: "/#about" },
    { text: "Practice Areas", href: "/#practice" },
    { text: "Contact", href: "/contact" }
  ]
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isExternalOrHash = (href: string) => {
    return href.startsWith('#') || href.startsWith('http') || href.includes('/#');
  };

  const renderNavLink = (link: NavLink, index: number, isMobile: boolean = false) => {
    const className = isMobile ? "navbar-menu-link" : "navbar-link";
    const style = isMobile ? { animationDelay: `${index * 0.1}s` } : undefined;

    if (isExternalOrHash(link.href)) {
      return (
        <a
          key={index}
          href={link.href}
          className={className}
          onClick={isMobile ? closeMenu : undefined}
          style={style}
        >
          {link.text}
        </a>
      );
    }

    return (
      <Link
        key={index}
        to={link.href}
        className={className}
        onClick={isMobile ? closeMenu : undefined}
        style={style}
      >
        {link.text}
      </Link>
    );
  };

  return (
    <>
      <nav className={`navbar ${isScrolled ? 'navbar-scrolled' : ''}`}>
        <div className="navbar-container">
          {/* Logo Section */}
          <Link to="/" className="navbar-logo" onClick={closeMenu}>
            <span className="navbar-logo-text">{logoText}</span>
          </Link>

          {/* Desktop Navigation Links */}
          <div className="navbar-links-desktop">
            {navLinks.map((link, index) => renderNavLink(link, index, false))}
          </div>

          {/* CTA Button */}
          {isExternalOrHash(ctaHref) ? (
            <a href={ctaHref} className="navbar-cta">
              {ctaText}
            </a>
          ) : (
            <Link to={ctaHref} className="navbar-cta">
              {ctaText}
            </Link>
          )}

          {/* Hamburger Menu Button */}
          <button
            className={`navbar-hamburger ${isMenuOpen ? 'active' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`navbar-menu-overlay ${isMenuOpen ? 'active' : ''}`}>
        <div className="navbar-menu-content">
          {/* Mobile Logo */}
          <div className="navbar-menu-header">
            <Link to="/" className="navbar-menu-logo" onClick={closeMenu}>
              <span className="navbar-menu-logo-text">{logoText}</span>
            </Link>
          </div>

          {/* Mobile Links */}
          <div className="navbar-menu-links">
            {navLinks.map((link, index) => renderNavLink(link, index, true))}
          </div>

          {/* Mobile CTA */}
          {isExternalOrHash(ctaHref) ? (
            <a 
              href={ctaHref} 
              className="navbar-menu-cta"
              onClick={closeMenu}
            >
              {ctaText}
            </a>
          ) : (
            <Link 
              to={ctaHref} 
              className="navbar-menu-cta"
              onClick={closeMenu}
            >
              {ctaText}
            </Link>
          )}
        </div>
      </div>

      {/* Backdrop */}
      {isMenuOpen && (
        <div 
          className="navbar-backdrop"
          onClick={closeMenu}
        />
      )}
    </>
  );
};

export default Navbar;

